import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { rushGameInitialState, rushGameReducer, RushGameState } from "../../reducers/rush-game.reducer";
import { useImmerReducer } from "use-immer";
import { initial, update } from "lodash";

// Utility function to generate random data points for the demo
const generateRandomData = (numPoints: number): number[] => {
  return Array.from({ length: numPoints }, () =>
    Math.floor(Math.random() * 100000)
  );
};

const getLastBTCValue = () => {
  return Math.floor(Math.random() * 100000);
};

const computeTradeProfitPercent = (startPrice: number, endPrice: number, isTradingUp: boolean) => {
  const profit = isTradingUp ? endPrice - startPrice : startPrice - endPrice;
  const profitPercent = (profit / startPrice) * 100;
  return profitPercent;
}

const RushChart = forwardRef((prop, ref) => {

  const initialSerie = useRef<number[]>([]);

  const backgroundRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const isTrading = useRef<boolean>(false);
  const isTradingUp = useRef<boolean>(false);

  const tradingValue = useRef<number>(0);
  const tradingPoint = useRef<any>(null);

  const currentValue = useRef<number>(0);
  const currentPoint = useRef<any>(null);

  const lastBTCValue = useRef<number>(0);

  let circle: any;
  let mainRec: any;
  let labelTxt: any;

  let tradeIcon: any;
  let tradeIconUp: any;
  let tradeIconDown: any;

  // let tradePoint: any;
  // let tradePointCircle: any;
  // let tradePointIcon: any;

  let tradePointRef = useRef<any>(null);
  let tradePointCircleRef = useRef<any>(null);
  let tradePointIconRef = useRef<any>(null);

  // let plotLine: any;
  let plotLineRef = useRef<any>(null);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
    chart: {
      type: "area",
      backgroundColor: "transparent",
      // animation: Highcharts.svg, // Enable animation for updates
      animation: true,
      // marginRight: 100,
      // spacingRight: 100,
      // spacingBottom: 0,
      height: 176,
      // spacingTop: 0,
      spacingLeft: 0, // Reset X axis SVG ELement
    },
    title: {
      text: "A", // Title influence the chart height
      align: "left",
      style: {
        color: "rgba(0, 0, 0, 0)",
        // fontSize: "12px",
        //   fontFamily: "Roboto,sans-serif",
        //   fontWeight: "700",
      },
    },
    xAxis: {
      maxPadding: 0.55,
      // tickAmount: 10,
      // tickWidth: 1,
      type: "linear",
      // tickInterval: 1,
      title: {
        text: "",
      },
      // gridLineColor: "rgba(255,255,255,1)",
      // gridLineWidth: 1,
      // visible: false,
      labels: {
        style: {
          // display: "none",
        },
      },
      gridLineColor: "#FFFFFF",
      gridLineWidth: 1,
      visible: false,
      // tickLength: 0,
      // tickAmount:10,
      // tickWidth:10,
    },
    yAxis: {
      tickAmount: 5,
      // tickLength: 30,
      tickPixelInterval: 30,
      title: {
        text: "",
      },
      opposite: true,
      labels: {
        align: "right",
        format: "{value:.0f}",
        x: 0,
        style: {
          color: "rgba(255, 255, 255, 0.3)",
          fontSize: "10px",
          fontFamily: "Roboto,sans-serif",
          fontWeight: "700",
        },
      },
      grid: {},
      gridLineColor: "rgba(255,255,255,0.2)",
      gridLineWidth: 0,
    },
    series: [
      {
        name: "BTC",
        type: "areaspline",
        // data: generateRandomData(20), // Initial 20 data points
        data: [], // Initial 20 data points
        marker: {
          enabled: false,
        },
      },
    ],
    tooltip: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      areaspline: {
        lineWidth: 1,
        lineColor: "rgba(255, 255, 255, 1)",
        color: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, "rgba(255, 255, 255, 0.2)"],
            [1, "rgba(255, 255, 255, 0.0)"],
          ],
        },
        states: {
          hover: {
            lineWidth: 1,
          },
        },
        threshold: null,
      },
    },
    credits: {
      enabled: false,
    },
  });

  function drawTradingLineAtValue(value: number) {

    const chart = chartRef.current?.chart;

    if (!chart) { return; }

    const renderer = chart?.renderer;

    plotLineRef.current = chart.yAxis[0].addPlotLine({
      color: "rgba(255, 255, 255, 0.7)",
      dashStyle: "Dash",
      width: 1,
      value: value,
      zIndex: 0,
    });

  }

  function drawTradingInfo(dataPoint: any) {

    // console.log('dataPoint: ', dataPoint);

    const chart = chartRef.current?.chart;

    if (!chart) { return; }

    const renderer = chart?.renderer;

    // PlotLine
    // plotLine = chart.yAxis[0].addPlotLine({
    plotLineRef.current = chart.yAxis[0].addPlotLine({
      color: "rgba(255, 255, 255, 0.7)",
      dashStyle: "Dash",
      width: 1,
      value: dataPoint.y,
      zIndex: 0,
    });

    // Trade Point Circle and Trade Point Icon
    tradePointCircleRef.current = renderer
      .circle(14, 14, 6)
      .attr({ fill: "black", id: "tradePointCircle", zIndex: 3 })
      .add();

    const imgHref = isTradingUp.current ? "/images/rush-game/white-up.png" : "/images/rush-game/white-down.png";

    tradePointIconRef.current = renderer
      .image(process.env.REACT_APP_STATIC_ASSETS + imgHref, 0, 0, 7, 7)
      .attr({ id: "tradePointIcon", zIndex: 3})
      .add();

    tradePointCircleRef.current.animate(
      {
        x: tradingPoint.current.plotX,
        y: tradingPoint.current.plotY + 47,
      },
      { duration: 10 }
    );

    tradePointIconRef.current.animate(
      {
        x: tradingPoint.current.plotX - 3.5,
        y: tradingPoint.current.plotY + 47 - 3.5,
      },
      { duration: 10 }
    );

  }

  function removeTradingInfo() {

    // plotLineRef.current ? console.log('plotLine') : console.log('no plotLine');

    if (plotLineRef.current) {
      plotLineRef.current.destroy();
      plotLineRef.current = null;
    }

    if (tradePointCircleRef.current) {
      tradePointCircleRef.current.destroy();
      tradePointCircleRef.current = null;
    }

    if (tradePointIconRef.current) {
      tradePointIconRef.current.destroy();
      tradePointIconRef.current = null;
    }

  }

  function destroyAll() {

    if(circle){
      circle.destroy();
      circle = null;
    }

    if(mainRec){
      mainRec.destroy();
      mainRec = null;
    }

    if(labelTxt){
      labelTxt.destroy();
      labelTxt = null;
    }

    if(tradeIcon){
      tradeIcon.destroy();
      tradeIcon = null;
    }

    if(tradeIconUp){
      tradeIconUp.destroy();
      tradeIconUp = null;
    }

    if(tradeIconDown){
      tradeIconDown.destroy();
      tradeIconDown = null;
    }

  }

  useImperativeHandle(ref, () => ({

    initBTCPrices(BTCPrices: number[]) {
      // console.log('Init serie with: ', BTCPrices);
      initialSerie.current = BTCPrices;
    },

    start: (isTradingUpValue: boolean) => {
      // console.log("started with current value", currentValue.current);
      isTrading.current = true;
      isTradingUp.current = isTradingUpValue;
      tradingValue.current = currentValue.current;

      backgroundRef.current?.classList.remove("background");
      backgroundRef.current?.classList.add("green-background");

      tradingPoint.current = currentPoint.current;
      drawTradingInfo(tradingPoint.current);
    },

    updateCurrentValue: (BTCPrice: number) => {
      lastBTCValue.current = BTCPrice;
    },

    load: (previousGameSession: RushGameState) => {
      // console.log("loaded with current value", previousGameSession.tradePrice);

      if(!isTrading.current) {

        isTrading.current = true;
        isTradingUp.current = previousGameSession.isTradingUp;
        tradingValue.current = previousGameSession.tradePrice;

        backgroundRef.current?.classList.remove("background");
        backgroundRef.current?.classList.add("green-background");

        // No Ref Circle to trading point on reload but draw a line
        drawTradingLineAtValue(tradingValue.current);

      }

    },

    end: () => {
      // console.log("ended");
      isTrading.current = false;
      isTradingUp.current = false;
      tradingValue.current = 0;
      tradingPoint.current = null;

      backgroundRef.current?.classList.remove(
        backgroundRef.current?.classList.value
      );

      backgroundRef.current?.classList.add("background");

      removeTradingInfo();
    },

    getCurrentValue: () => {
      return currentValue.current;
    }

  }));

  // Function to update the chart every second
  useEffect(() => {
    const interval = setInterval(() => {
      const chart = chartRef.current?.chart;

      if (!chart || !initialSerie.current) {
        return;
      }

      // Get last BTC value
      // const newValue = getLastBTCValue();
      // currentValue.current = newValue;

      const newValue = lastBTCValue.current;
      currentValue.current = newValue;

      if(!chart.series[0].data.length) {
        chart.series[0].setData(initialSerie.current);
      }

      // Shift the data, remove the first point, and add the new one
      const series = chart.series[0];
      series.addPoint(newValue, true, true);

      currentPoint.current = series.data[series.data.length - 1];

      const renderer = chart.renderer;

      if (!circle) {
        circle = renderer
          .circle(10, 10, 4)
          .attr({ fill: "rgba(255, 255, 255, 0.8)", id: "lastPoint" })
          .add();
      }

      if (!mainRec) {
        mainRec = renderer
          .rect(0, 0, 82, 26, 4, 0)
          .attr({
            fill: "white",
            id: "mainRect",
            stroke: "black",
            zIndex: 1,
          })
          .add();
      }

      if (!labelTxt) {
        labelTxt = renderer
          .text("Label", 0, 0)
          .css({
            color: "black",
            fontSize: "12px",
            fontWeight: "bold",
            fontFamily: "Arial",
          })
          .attr({
            fill: "black",
            id: "labelTxt",
            zIndex: 2,
          })
          .add();
      }

      const tradeProfitPercent = computeTradeProfitPercent(
        tradingValue.current,
        newValue,
        isTradingUp.current
      );

      const winning = tradeProfitPercent >= 0;

      if (!tradeIcon) {
        tradeIcon = renderer
          .image(process.env.REACT_APP_STATIC_ASSETS + "/images/rush-game/tanji-coin.png", 0, 0, 16, 16)
          .attr({
            id: "tradeIcon",
            zIndex: 2,
          })
          .add()
          .hide();

        tradeIconUp = renderer
          .image(process.env.REACT_APP_STATIC_ASSETS +"/images/rush-game/up.png", 0, 0, 16, 16)
          .attr({
            id: "tradeIconUp",
            zIndex: 2,
          })
          .add()
          .hide();

        tradeIconDown = renderer
          .image(process.env.REACT_APP_STATIC_ASSETS + "/images/rush-game/down.png", 0, 0, 16, 16)
          .attr({
            id: "tradeIconDown",
            zIndex: 2,
          })
          .add()
          .hide();
      }

      const lastPointX = series.data[series.data.length - 1].plotX || 0;
      const lastPointY = series.data[series.data.length - 1].plotY || 0;

      // const circle = chart.renderer.circle(999, newValue, 4).attr({ fill: "red", id: "lastPoint" }).add();
      // circle.animate({ x: series.data[series.data.length - 1].x, y: newValue }, 100);

      circle?.animate(
        {
          x: lastPointX,
          y: lastPointY + 47,
        },
        { duration: 500 }
      );

      mainRec?.animate(
        {
          x: lastPointX + 8,
          y: lastPointY + 47 - 13,
        },
        { duration: 500 }
      );

      let textLabel = newValue.toFixed(2);
      let labelColor = "black";

      const profitDisplayed = Math.abs(tradeProfitPercent).toFixed(3);

      const root = document.documentElement;

      if (isTrading.current && winning) {
        textLabel = "+" + profitDisplayed + "%";
        labelColor = "green";

        tradeIcon.hide();
        tradeIconDown.hide();
        tradeIconUp.show();

        backgroundRef.current?.classList.remove(
          backgroundRef.current?.classList.value
        );
        backgroundRef.current?.classList.add("green-background");

      } else if (isTrading.current && !winning) {
        textLabel = "-" + profitDisplayed + "%";
        labelColor = "red";

        tradeIcon.hide();
        tradeIconDown.show();
        tradeIconUp.hide();

        backgroundRef.current?.classList.remove(
          backgroundRef.current?.classList.value
        );
        backgroundRef.current?.classList.add("red-background");

      } else {

        tradeIconDown.hide();
        tradeIconUp.hide();
        tradeIcon.show();

        backgroundRef.current?.classList.remove(
          backgroundRef.current?.classList.value
        );
        backgroundRef.current?.classList.add("background");

      }

      // if(!isTrading.current) {
      //   tradeIcon.show();
      //   tradeIconUp.hide();
      //   tradeIconDown.hide();
      // } else if ( isTradingUp.current ) {
      //   tradeIcon.hide();
      //   tradeIconUp.show();
      //   tradeIconDown.hide();
      // } else if ( !isTradingUp.current ) {
      //   tradeIcon.hide();
      //   tradeIconUp.hide();
      //   tradeIconDown.show();
      // }

      const fontSize = tradeProfitPercent > 1000 ? "10px" : "11px";

      labelTxt
        ?.attr({ text: textLabel })
        .css({
          // font: fontSize,
          fontSize: fontSize,
          color: labelColor,
          transitionDelay: "1s",
          transition: "all 0.75s ease",
        })
        .animate(
          {
            x: lastPointX + 8 + 15 + 8,
            y: lastPointY + 47 + 4,
          },
          { duration: 500 }
        );

      tradeIcon?.animate(
        {
          x: lastPointX + 8 + 4,
          y: lastPointY + 47 - 8,
        },
        { duration: 500 }
      );

      tradeIconUp?.animate(
        {
          x: lastPointX + 8 + 4,
          y: lastPointY + 47 - 8,
        },
        { duration: 500 }
      );

      tradeIconDown?.animate(
        {
          x: lastPointX + 8 + 4,
          y: lastPointY + 47 - 8,
        },
        { duration: 500 }
      );

      // Trading Point
      if (tradingPoint.current && tradingPoint.current.plotX > 0) {
        tradePointCircleRef.current?.animate(
          {
            x: tradingPoint.current.plotX,
            y: tradingPoint.current.plotY + 47,
          },
          { duration: 500 }
        );
        tradePointIconRef.current?.animate(
          {
            x: tradingPoint.current.plotX - 3.5,
            y: tradingPoint.current.plotY + 47 - 3.5,
          },
          { duration: 500 }
        );
      } else if (tradingPoint.current) {
        tradePointCircleRef.current.destroy();
        tradePointCircleRef.current = null;
        tradePointIconRef.current.destroy();
        tradePointIconRef.current = null;
        tradingPoint.current = null;
      }

    }, 1000); // Update every second

    return () => {
      // Cleanup interval on component unmount
      clearInterval(interval);

      // Cleanup chart elements
      destroyAll();
      removeTradingInfo();

    };
  }, []);

  return (
    // <div className="red-background">
    <div id="backGroundTransition" ref={backgroundRef} className="background">
      <div className="grid-pattern">
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
        />
      </div>
    </div>
  );
});

export default RushChart;